(() => {
  const root = document.documentElement;
  const body = document.body;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  if (
    !body?.hasAttribute('data-briefbuilt-motion') ||
    reduceMotion.matches ||
    !('IntersectionObserver' in window) ||
    typeof Element.prototype.animate !== 'function'
  ) {
    return;
  }

  const activeAnimations = new Set();
  const observedElements = new Set();

  const groups = [
    {
      selector: '.hero__copy > .eyebrow, .hero__copy > h1, .hero__copy > .lead, .hero__copy > .button-row, .hero__copy > .hero__proof',
      distance: 18,
      duration: 520,
      stagger: 70
    },
    {
      selector: '.hero__visual',
      distance: 22,
      duration: 640,
      stagger: 0,
      scale: 0.985
    },
    {
      selector: '.process-card',
      distance: 24,
      duration: 560,
      stagger: 85
    },
    {
      selector: '.package',
      distance: 24,
      duration: 560,
      stagger: 85
    },
    {
      selector: '.add-on',
      distance: 24,
      duration: 560,
      stagger: 85
    },
    {
      selector: '.store-preview__included li',
      distance: 16,
      duration: 480,
      stagger: 65
    },
    {
      selector: '.store-preview__steps li',
      distance: 16,
      duration: 480,
      stagger: 65
    },
    {
      selector: '.about-brand__values > span',
      distance: 16,
      duration: 480,
      stagger: 65
    },
    {
      selector: '.faq__item',
      distance: 14,
      duration: 440,
      stagger: 40
    },
    {
      selector: '.disclosure__grid article',
      distance: 14,
      duration: 440,
      stagger: 40
    },
    {
      selector: '.product__media, .product__info',
      distance: 20,
      duration: 580,
      stagger: 90
    },
    {
      selector: '.package-timeline__steps > *, .product-add-on',
      distance: 16,
      duration: 480,
      stagger: 55
    },
    {
      selector: '.page-main__intro, .page-main__jumps, .page-main > .rte',
      distance: 18,
      duration: 520,
      stagger: 70
    },
    {
      selector: '.collection__link, .blog-card',
      distance: 20,
      duration: 520,
      stagger: 65
    },
    {
      selector: '.blog-main__header, .contact__grid > *, .error-page__inner > *, .secure-feedback-notice__card',
      distance: 18,
      duration: 540,
      stagger: 70
    },
    {
      selector: '.article-main__hero-copy, .article-main__visual, .article-main__layout > *, .article-main__footer',
      distance: 18,
      duration: 540,
      stagger: 70
    },
    {
      selector: '.entity-page__main, .entity-page__aside',
      distance: 20,
      duration: 560,
      stagger: 90
    },
    {
      selector: '.traffic-landing__hero > *, .traffic-landing__detail, .traffic-landing__steps > *, .traffic-landing__packages > *, .traffic-landing__questions > *, .traffic-landing__final',
      distance: 18,
      duration: 520,
      stagger: 60
    },
    {
      selector: '.resource-hub__header, .resource-hub__groups > *, .resource-hub__cards > *, .resource-hub__cta',
      distance: 18,
      duration: 520,
      stagger: 60
    },
    {
      selector: '.decision-resource__inner > *, .decision-resource-faq__item',
      distance: 18,
      duration: 520,
      stagger: 60
    },
    {
      selector: '.dropship-guide__inner > *, .methodology-page__inner > *, .readiness-tool__header, .readiness-tool__form, .readiness-tool__result, .readiness-tool__sources',
      distance: 18,
      duration: 520,
      stagger: 60
    },
    {
      selector: '.case-study__inner > *, .case-study__facts > *, .case-study__sections > *, .case-study__cta',
      distance: 18,
      duration: 520,
      stagger: 60
    },
    {
      selector: '.seo-intent-links__grid > *, .work-card, .testimonials__grid > *',
      distance: 18,
      duration: 500,
      stagger: 60
    },
    {
      selector: '.cart-items, .cart-summary, .cart-support, .cart-soft-launch, .empty-state, .cart-warning, .cart-next',
      distance: 18,
      duration: 520,
      stagger: 70
    },
    {
      selector: '.cta__box',
      distance: 18,
      duration: 560,
      stagger: 0,
      scale: 0.99
    }
  ];

  const observer = new IntersectionObserver(
    entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;

        const element = entry.target;
        observer.unobserve(element);
        observedElements.delete(element);

        const distance = Number(element.dataset.motionDistance || 18);
        const duration = Number(element.dataset.motionDuration || 520);
        const delay = Number(element.dataset.motionDelay || 0);
        const scale = Number(element.dataset.motionScale || 1);
        const animation = element.animate(
          [
            {
              opacity: 0,
              transform: `translate3d(0, ${distance}px, 0) scale(${scale})`
            },
            {
              opacity: 1,
              transform: 'translate3d(0, 0, 0) scale(1)'
            }
          ],
          {
            duration,
            delay,
            easing: 'cubic-bezier(.2,.75,.25,1)',
            fill: 'both'
          }
        );

        activeAnimations.add(animation);
        animation.finished
          .catch(() => {})
          .finally(() => {
            activeAnimations.delete(animation);
            animation.cancel();
          });
      });
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
  );

  groups.forEach(group => {
    document.querySelectorAll(group.selector).forEach((element, index) => {
      if (observedElements.has(element)) return;
      element.dataset.motionDistance = String(group.distance);
      element.dataset.motionDuration = String(group.duration);
      element.dataset.motionDelay = String(Math.min(index * group.stagger, 280));
      element.dataset.motionScale = String(group.scale || 1);
      observedElements.add(element);
      observer.observe(element);
    });
  });

  const disableMotion = event => {
    if (!event.matches) return;
    observer.disconnect();
    observedElements.clear();
    activeAnimations.forEach(animation => animation.cancel());
    activeAnimations.clear();
    root.removeAttribute('data-motion-active');
  };

  reduceMotion.addEventListener?.('change', disableMotion);
  root.setAttribute('data-motion-active', 'true');
})();
